import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import ThemeToggle from './ThemeToggle';
import Logo from './Logo';
import { formatSiteAccessRoles } from '../constants/siteAccessRoles';
import { SERVER_URL } from '../config';
import {
  isoToRuDisplay,
  parseBirthDateInput
} from '../utils/birthDateInput';
import './Auth.css';

const Register = ({ setIsAuthenticated }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [step, setStep] = useState(1); // 1: Discord OAuth, 2: Role check, 3: Registration
  const [discordData, setDiscordData] = useState({
    userId: '',
    username: '',
    discriminator: '',
    email: ''
  });
  const [userRoles, setUserRoles] = useState([]);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    birthDate: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [discordAuthing, setDiscordAuthing] = useState(false);
  const [birthInputText, setBirthInputText] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    if (step === 3) {
      if (formData.birthDate) {
        setBirthInputText(isoToRuDisplay(formData.birthDate));
      } else {
        setBirthInputText('');
      }
    }
  }, [step, formData.birthDate]);

  // Функция для применения фона темы
  const applyThemeBackground = useCallback(() => {
    const theme = document.documentElement.getAttribute('data-theme') || 'dark';
    const publicUrl = process.env.PUBLIC_URL || '';
    const darkBg = `${publicUrl}/backgrounds/background-dark.png`;
    const lightBg = `${publicUrl}/backgrounds/background-light.png`;
    
    const bgUrl = theme === 'light' ? lightBg : darkBg;
    document.body.style.backgroundImage = `url(${bgUrl})`;
    document.body.style.backgroundColor = '';
    document.body.style.backgroundSize = 'cover';
    document.body.style.backgroundPosition = 'center';
    document.body.style.backgroundRepeat = 'no-repeat';
    document.body.style.backgroundAttachment = 'fixed';
    const mobileBg = document.getElementById('mobile-bg');
    if (mobileBg) mobileBg.style.backgroundImage = `url(${bgUrl})`;
  }, []);

  // Применение фона и блокировка overscroll на мобильных
  useEffect(() => {
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.overscrollBehavior = 'none';
    document.body.style.overflow = 'hidden';
    document.body.style.overscrollBehavior = 'none';

    applyThemeBackground();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
          applyThemeBackground();
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    return () => {
      document.documentElement.style.overflow = '';
      document.documentElement.style.overscrollBehavior = '';
      document.body.style.overflow = '';
      document.body.style.overscrollBehavior = '';
      observer.disconnect();
    };
  }, [applyThemeBackground]);

  // Обработка callback от Discord OAuth
  useEffect(() => {
    const errorParam = searchParams.get('error');
    const successParam = searchParams.get('success');
    const stepParam = searchParams.get('step');
    const sessionToken = searchParams.get('session');
    const token = searchParams.get('token');
    const discordId = searchParams.get('discord_id');
    const rolesParam = searchParams.get('roles');

    if (errorParam) {
      if (errorParam === 'not_member') {
        setError('Вы не являетесь участником Discord сервера');
      } else if (errorParam === 'no_role') {
        let errorMsg = `У вас нет необходимой роли для регистрации. Требуемые роли: ${formatSiteAccessRoles()}.`;
        if (rolesParam) {
          try {
            const roles = JSON.parse(decodeURIComponent(rolesParam));
            setUserRoles(roles);
            errorMsg += ` Ваши роли: ${roles.join(', ') || 'нет ролей'}`;
          } catch (e) {
            console.error('Error parsing roles:', e);
          }
        }
        setError(errorMsg);
      } else if (errorParam === 'oauth_failed') {
        setError('Ошибка при авторизации через Discord. Попробуйте снова.');
      } else if (errorParam === 'already_registered') {
        const messageParam = searchParams.get('message');
        const message = messageParam ? decodeURIComponent(messageParam) : 'Вы уже зарегистрированы. Войдите через email и пароль на странице входа.';
        setError(message);
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else if (errorParam === 'role_lost') {
        setError('Ваша роль была удалена. Вы потеряли доступ к системе.');
      } else if (errorParam === 'bot_not_configured') {
        setError('Discord бот не настроен. Обратитесь к администратору.');
      } else if (errorParam === 'bot_not_ready') {
        setError('Discord бот не подключен. Дождитесь подключения бота.');
      } else if (errorParam === 'bot_error') {
        setError('Ошибка при проверке через Discord бота. Попробуйте позже.');
      } else if (errorParam === 'no_code') {
        setError('Не получен код авторизации от Discord.');
      }
      setSearchParams({});
    }

    if (stepParam === '3' && sessionToken) {
      // Расшифровка данных из сессионного токена
      try {
        const decoded = JSON.parse(atob(sessionToken.split('.')[1]));
        setDiscordData({
          userId: decoded.userId || discordId,
          username: decoded.username,
          discriminator: decoded.discriminator,
          email: decoded.email || ''
        });
        setUserRoles(decoded.roles || []);
        setFormData(prev => ({
          ...prev,
          email: decoded.email || prev.email
        }));
        setStep(3);
        setSearchParams({});
      } catch (e) {
        console.error('Error decoding session token:', e);
        setError('Ошибка при обработке данных Discord');
      }
    }
  }, [searchParams, setSearchParams, navigate, setIsAuthenticated]);

  // Авторизация через Discord
  const handleDiscordAuth = () => {
    setDiscordAuthing(true);
    window.location.href = `${SERVER_URL}/api/auth/discord`;
  };


  // Step 3: Final registration
  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Пароли не совпадают');
      return;
    }

    if (formData.password.length < 6) {
      setError('Пароль должен содержать минимум 6 символов');
      return;
    }

    const birthParsed = parseBirthDateInput(birthInputText.trim() || formData.birthDate);
    if (!birthParsed.ok) {
      setError(birthParsed.error);
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post('/api/auth/register', {
        email: formData.email,
        password: formData.password,
        discordUserId: discordData.userId,
        discordUsername: discordData.username,
        discordDiscriminator: discordData.discriminator,
        roles: userRoles,
        birthDate: birthParsed.iso
      });

      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setIsAuthenticated(true);
      navigate('/discordmoderation/overview');
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка при регистрации');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (step === 1) {
      setDiscordData({
        ...discordData,
        [name]: value
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
    setError('');
  };

  const applyRegisterBirthBlur = () => {
    const trimmed = birthInputText.trim();
    if (!trimmed) {
      setFormData((prev) => ({ ...prev, birthDate: '' }));
      return;
    }
    const p = parseBirthDateInput(trimmed);
    if (p.ok) {
      setFormData((prev) => ({ ...prev, birthDate: p.iso }));
      setBirthInputText(isoToRuDisplay(p.iso));
    }
  };

  return (
    <div className="auth-container">
      <ThemeToggle />
      <div className="auth-box">
        <div className="auth-left-panel">
          <div className="auth-header">
            <Logo variant="auth" style={{ marginBottom: '20px' }} />
            <p>
              {step === 1 && 'Проверка участия в Discord сервере'}
              {step === 2 && 'Проверка ролей'}
              {step === 3 && 'Завершите регистрацию'}
            </p>
          </div>

          {error && <div className="error-message">{error}</div>}

          {/* Step 1: Discord OAuth */}
          {step === 1 && (
            <div className="auth-form">
              <div className="form-group">
                <p className="discord-info-text">
                  Нажмите кнопку ниже, чтобы проверить ваше участие в Discord сервере и наличие необходимых ролей. После проверки вы сможете завершить регистрацию, указав email и пароль.
                </p>
              </div>
              <button 
                type="button"
                onClick={handleDiscordAuth} 
                className="auth-button discord-auth-button" 
                disabled={discordAuthing}
                style={{
                  background: '#ff8000',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px'
                }}
              >
                {discordAuthing ? (
                  <>
                    <span>Проверка...</span>
                  </>
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 71 55" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g clipPath="url(#clip0)">
                        <path d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 48.9718 48.5383C50.038 50.6034 51.2554 52.5699 52.5959 54.435C52.6519 54.5139 52.7526 54.5477 52.845 54.5195C58.6464 52.7249 64.529 50.0174 70.6019 45.5576C70.6551 45.5182 70.6887 45.459 70.6943 45.3942C72.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.3451 34.1136 17.3451 30.1693C17.3451 26.225 20.1717 23.0133 23.7259 23.0133C27.308 23.0133 30.1626 26.2532 30.1066 30.1693C30.1066 34.1136 27.28 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.9371 34.1136 40.9371 30.1693C40.9371 26.225 43.7636 23.0133 47.3178 23.0133C50.9 23.0133 53.7545 26.2532 53.6986 30.1693C53.6986 34.1136 50.9 37.3253 47.3178 37.3253Z" fill="currentColor"/>
                      </g>
                    </svg>
                    <span>Проверить через Discord</span>
                  </>
                )}
              </button>
            </div>
          )}

          {/* Step 3: Registration */}
          {step === 3 && (
            <form onSubmit={handleRegister} className="auth-form">
              <div className="form-group">
                <p className="discord-info-text" style={{ marginBottom: '10px', textAlign: 'left' }}>
                  Discord: <strong>{discordData.username}#{discordData.discriminator}</strong>
                </p>
                <p className="discord-roles-text">
                  Роли: {userRoles.join(', ') || 'Нет ролей'}
                </p>
              </div>

              <div className="form-group">
                <label htmlFor="email">EMAIL</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email || discordData.email}
                  onChange={handleChange}
                  required
                  placeholder="Email"
                  disabled={!!discordData.email}
                />
              </div>

              <div className="form-group auth-birth-group">
                <label htmlFor="register-birth-input">ДАТА РОЖДЕНИЯ</label>
                <input
                  type="text"
                  id="register-birth-input"
                  name="registerBirthManual"
                  className="auth-birth-manual-input"
                  value={birthInputText}
                  onChange={(e) => {
                    setBirthInputText(e.target.value);
                    setError('');
                  }}
                  onBlur={applyRegisterBirthBlur}
                  placeholder="ДД.ММ.ГГГГ"
                  autoComplete="bday"
                />
                <p className="discord-info-text" style={{ marginTop: '8px', fontSize: '12px', textAlign: 'left', opacity: 0.85 }}>
                  Укажите реальную дату — так команда сможет поздравлять с днём рождения.
                </p>
              </div>

              <div className="form-group">
                <label htmlFor="password">ПАРОЛЬ</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  placeholder="Пароль (минимум 6 символов)"
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">ПОДТВЕРДИТЕ ПАРОЛЬ</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  placeholder="Подтвердите пароль"
                />
              </div>

              <button type="submit" className="auth-button" disabled={loading}>
                {loading ? 'Регистрация...' : 'Зарегистрироваться'}
              </button>
              <button 
                onClick={() => {
                  setStep(1);
                  setDiscordData({ userId: '', username: '', discriminator: '', email: '' });
                  setUserRoles([]);
                  setFormData({ email: '', password: '', confirmPassword: '', birthDate: '' });
                  setBirthInputText('');
                }} 
                className="auth-button" 
                style={{ marginTop: '10px', background: 'rgba(255, 255, 255, 0.1)' }}
              >
                Назад
              </button>
            </form>
          )}

          <div className="auth-footer">
            <p>
              Уже есть аккаунт?{' '}
              <Link to="/login" className="auth-link">
                Войти
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
